import * as pc from "playcanvas";

import type { Number3, Number4 } from "../types";

export interface ReadonlyColor {
  get r(): number;
  get g(): number;
  get b(): number;
  get a(): number;

  clone(): Color;
  copyToArray(array: Array<number> | Float32Array | Float64Array, offset?: number): this;
  equals(rhs: ReadonlyColor): boolean;
  toArray(): Number4;
  toString(alpha: boolean): string;

  // Convert for use in external JS code.
  // This is unsafe and should only be used when necessary.
  unsafeCast(): Color;
}

declare module "playcanvas" {
  interface Color extends ReadonlyColor {
    r: number;
    g: number;
    b: number;
    a: number;

    copy(rhs: ReadonlyColor): this;
    copyToArray(array: Array<number> | Float32Array | Float64Array, offset?: number): this;
    equals(rhs: ReadonlyColor): boolean;
    fromString(hex: string): this;
    gamma(src?: ReadonlyColor): this;
    lerp(lhs: ReadonlyColor, rhs: ReadonlyColor, alpha: number): this;
    linear(src?: ReadonlyColor): this;
    mulScalar(scalar: number): this;
    set(r: number, g: number, b: number, a?: number): this;
    toArray(): Number4;

    recycle(): void;
  }
}

pc.Color.prototype.recycle = function () {
  pool.push(this);
};

pc.Color.prototype.unsafeCast = function () {
  return this;
};

const pool: Color[] = [];

// @ts-expect-error - Override static constants with ReadonlyVector3 type (intentionally incompatible with base class)
export class Color extends pc.Color {
  static override BLACK = this.makeReadonly(0, 0, 0, 1);
  static override BLUE = this.makeReadonly(0, 0, 1, 1);
  static override CYAN = this.makeReadonly(0, 1, 1, 1);
  static override GRAY = this.makeReadonly(0.5, 0.5, 0.5, 1);
  static override GREEN = this.makeReadonly(0, 1, 0, 1);
  static override MAGENTA = this.makeReadonly(1, 0, 1, 1);
  static override RED = this.makeReadonly(1, 0, 0, 1);
  static override WHITE = this.makeReadonly(1, 1, 1, 1);
  static override YELLOW = this.makeReadonly(1, 1, 0, 1);

  static isInstance(value: unknown): value is Color {
    return value instanceof pc.Color;
  }

  /**
   * @summary Creates a new vector with the given components.
   */
  static make(r = 0, g = 0, b = 0, a = 1): Color {
    return pool.pop()?.set(r, g, b, a) ?? new Color(r, g, b, a);
  }

  /**
   * Create a readonly Vector that cannot be recycled.
   */
  static makeReadonly(r: number, g: number, b: number, a?: number): ReadonlyColor {
    return this.make(r, g, b, a);
  }

  /**
   * @summary Creates a new uninitialized vector.
   */
  static makeUninitialized() {
    return pool.pop() ?? new Color();
  }

  static fromArray(array: Readonly<Number4> | Readonly<Number3>) {
    return this.make(array[0], array[1], array[2], array.length > 3 ? array[3] : 1);
  }
}
